import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConstants } from '@apps/auth/auth.constants';
import { AppConstants } from '@apps/app.constants';
import { RootConstants } from '@apps/root/root.constants';
import { ImpersonateService } from '@apps/root/services/impersonate.service';

/**
 * SessionService - Service centralisé pour la gestion de session
 */
@Injectable({
    providedIn: 'root'
})
export class SessionService
{
    private readonly tokenKey = AuthConstants.TokenKey;
    private readonly loginUrl = AppConstants.LoginUrl;
    private readonly impersonateKey = RootConstants.ImpersonateKey;

    constructor (
        private router: Router,
        private impersonateService: ImpersonateService
    )
    {
        // Écouter les changements de localStorage entre onglets
        window.addEventListener('storage', (event) =>
        {
            if (event.key === this.impersonateKey && event.newValue === null)
            {
                this.impersonateService.unsetImpersonation();
            }
            if (event.key === this.tokenKey && event.newValue === null)
            {
                if (!this.router.url.includes('/auth/'))
                {
                    this.handleSessionExpired('Token supprimé depuis un autre onglet');
                }
            }
        });
    }

    /**
     * Nettoie la session utilisateur et redirige vers la page de connexion
     * 
     * Cette méthode assure :
     * 1. La suppression de l'impersonation si active (MUST BE FIRST)
     * 2. La suppression du token de localStorage
     * 3. La suppression de tout état local lié à la session
     * 4. La redirection vers la page de login
     * 
     * @param reason - Optionnel. Raison de la fin de session (pour logging)
     */
    handleSessionExpired (reason?: string): void
    {
        this.clearImpersonation();
        this.clearToken();
        this.clearSessionState();

        this.redirectToLogin();
    }

    clearToken (): void
    {
        localStorage.removeItem(this.tokenKey);
    }

    clearImpersonation (): void
    {
        this.impersonateService.unsetImpersonation();
    }

    clearSessionState (): void
    {
        sessionStorage.clear();
        
        if ('caches' in window)
        {
            caches.keys().then(names => names.forEach(name => caches.delete(name)));
        }
    }

    /**
     * Redirige vers la page de connexion
     * 
     * @param returnUrl - Optionnel. URL à retourner après connexion
     */
    redirectToLogin (returnUrl?: string): void
    {
        // Protection contre la boucle infinie : ne pas rediriger si déjà sur login
        if (this.router.url === this.loginUrl || this.router.url.includes('/auth/'))
        {
            return;
        }
        
        const url = returnUrl ? `${this.loginUrl}?returnUrl=${encodeURIComponent(returnUrl)}` : this.loginUrl;
        this.router.navigate([this.loginUrl]);
    }

    hasToken (): boolean
    {
        const token = localStorage.getItem(this.tokenKey);
        return token !== null && token !== undefined;
    }

    getToken (): string | null
    {
        return localStorage.getItem(this.tokenKey);
    }
}
